import React, {useState, useCallback, useMemo} from 'react';
import {List, Map} from 'immutable';

import {setUpStore, callTargetedAction} from 'client/framework';

import {SALUTATIONS} from 'resources/augmented/User/user.extensions';
import {Button, Input, Label, SelectField} from '@albert-io/atomic';
import notifier from '@albert-io/notifier';

import {completeAccountCompletion} from 'lib/OnboardingUtil/OnboardingUtil';
import errorCodes from 'client/errorCodes';
import ErrorStateStore from 'client/generic/Forms/ErrorState/ErrorState.store';
import formActions from 'client/generic/Forms/ErrorState/Form.actions';

import './assets/update-account.scss';
import '../../../AccountSettings/AccountProfile/account-profile.scss';
import SchoolAssociation from 'client/components/SchoolSearchModal/SchoolAssociation.react';

const errorStateStoreName = 'UpdateAccountErrorStateStore';

const startsWithUUID = new RegExp('^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{2}');

const UpdateAccount = ({user: initialUser, onComplete}) => {
  const [user, setUser] = useState(initialUser);
  const [school, setSchool] = useState(
    initialUser.hasSchool() ? initialUser.getSchool().getId() : null
  );
  const [hasIncomingSchool] = useState(initialUser.hasSchool());
  const [visitedFields, setVisitedFields] = useState({
    salutation: false,
    firstName: false,
    lastName: false,
    username: false,
    school: false
  });

  const getErrorStateStore = useCallback(() => {
    return setUpStore(ErrorStateStore, errorStateStoreName);
  }, []);

  const hasError = useCallback(
    (validatorName) => {
      return user.validators[validatorName]() !== null;
    },
    [user]
  );

  const hasSchoolError = useCallback(() => {
    if (hasIncomingSchool && !school) {
      return true;
    }
    return user.isTeacher() && !school;
  }, [hasIncomingSchool, school, user]);

  const getSchoolError = useCallback(() => {
    if (hasSchoolError()) {
      return 'School is a required field';
    }
    return null;
  }, [hasSchoolError]);

  const hasFormErrors = useCallback(
    (userToValidate) => {
      const validators = List([
        userToValidate.validators.getSalutation,
        userToValidate.validators.getFirstName,
        userToValidate.validators.getLastName,
        user.isSocialUser ? userToValidate.validators.getUsername : () => null
      ]);

      const errorList = validators.reduce<any>((acc, validator) => {
        return acc.concat(validator());
      }, List());

      return (
        errorList.some((value) => {
          return value !== null;
        }) || hasSchoolError()
      );
    },
    [user, hasSchoolError]
  );

  const markFieldAsVisited = useCallback((e) => {
    const fieldName = e.target.name;
    setVisitedFields((prev) => {
      if (prev[fieldName]) {
        return prev;
      }
      return {
        ...prev,
        [fieldName]: true
      };
    });
  }, []);

  const makeOnChangeForField = useCallback(
    (fieldName) => {
      return (e) => {
        if (fieldName === 'username' && getErrorStateStore().getFormErrorForField(fieldName)) {
          callTargetedAction({
            name: formActions.CLEAR_FIELD_ERROR,
            targetStore: errorStateStoreName,
            payload: fieldName
          });
        }

        const {value} = e.target;
        setUser((prevUser) => prevUser[fieldName](value));
      };
    },
    [getErrorStateStore]
  );

  const setSalutation = useMemo(
    () => makeOnChangeForField('setSalutation'),
    [makeOnChangeForField]
  );
  const setFirstName = useMemo(() => makeOnChangeForField('setFirstName'), [makeOnChangeForField]);
  const setLastName = useMemo(() => makeOnChangeForField('setLastName'), [makeOnChangeForField]);
  const setUsername = useMemo(() => makeOnChangeForField('setUsername'), [makeOnChangeForField]);

  const updateUserInfo = useCallback(async () => {
    if (hasFormErrors(user)) {
      return;
    }

    if (!getErrorStateStore().getFormErrors().isEmpty()) {
      callTargetedAction({
        name: formActions.CLEAR_FORM_ERRORS,
        targetStore: errorStateStoreName
      });
    }

    let userUnderEdit;
    try {
      userUnderEdit = completeAccountCompletion(user);

      if (school) {
        if (!user.hasSchool()) {
          userUnderEdit = userUnderEdit.addRelationship({
            type: 'school_v4',
            relation: school
          });
        } else if (user.hasSchool()) {
          userUnderEdit = userUnderEdit.replaceRelationship({
            type: 'school_v4',
            relation: school
          });
        }
      }

      await Promise.all([userUnderEdit.save()]);

      onComplete();
    } catch (e: any) {
      if (e.status === 400) {
        const {errors} = e.response.body;
        const constraintViolations = errors
          .reduce((acc, error) => {
            if (error.code === errorCodes.BAD_REQUEST.VALIDATION_ERROR.UNIQUE_CONSTRAINT_VIOLATED) {
              return acc.push(error);
            }
            return acc;
          }, List())
          .reduce((acc, error) => {
            if (error.source.parameter === 'username') {
              return acc.push(
                Map({
                  field: 'username',
                  message: 'That username is already in use.'
                })
              );
            }
            return acc;
          }, List());

        callTargetedAction({
          name: formActions.SET_FORM_ERRORS,
          payload: constraintViolations,
          targetStore: errorStateStoreName
        });
      } else {
        notifier.notify(e, {
          message: 'Failed to update user account information',
          tags: 'UpdateAccount'
        });
      }
    }
  }, [user, school, hasFormErrors, getErrorStateStore, onComplete]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      markFieldAsVisited({target: {name: 'salutation'}});
      markFieldAsVisited({target: {name: 'firstName'}});
      markFieldAsVisited({target: {name: 'lastName'}});
      markFieldAsVisited({target: {name: 'username'}});
      markFieldAsVisited({target: {name: 'school'}});
      updateUserInfo();
    },
    [updateUserInfo, markFieldAsVisited]
  );

  const usernameError = getErrorStateStore().getFormErrorForField('username');

  const salutationInput = user.isTeacher() ? (
    <div>
      <SelectField
        name='salutation'
        label='Title'
        autoComplete='honorific-prefix'
        required
        value={user.getSalutation()}
        error={visitedFields.salutation && hasError('getSalutation')}
        onChange={setSalutation}
        onBlur={markFieldAsVisited}
        data-testid='update-account-salutation'
        options={[
          {label: '', value: '', disabled: true},
          ...SALUTATIONS.map((salutation) => ({
            label: salutation,
            value: salutation
          }))
        ]}
      />
    </div>
  ) : null;

  return (
    <form className='m-auto max-w-[500px] px-x2'>
      {salutationInput}
      <div className='my-x1 grid grid-cols-2 gap-x-x1'>
        <div>
          <Label>First Name</Label>
          <Input
            className='mt-x1'
            name='firstName'
            type='text'
            autoComplete='given-name'
            required
            maxLength={128}
            value={user.getFirstName()}
            error={visitedFields.firstName && hasError('getFirstName')}
            onChange={setFirstName}
            onBlur={markFieldAsVisited}
            onKeyDown={markFieldAsVisited}
          />
        </div>
        <div>
          <Label className='whitespace-nowrap'>Last Name (or School ID)</Label>
          <Input
            className='mt-x1'
            name='lastName'
            type='text'
            autoComplete='family-name'
            required
            maxLength={128}
            value={user.getLastName()}
            error={visitedFields.lastName && hasError('getLastName')}
            onChange={setLastName}
            onBlur={markFieldAsVisited}
            onKeyDown={markFieldAsVisited}
          />
        </div>
      </div>
      {user.isSocialUser && (
        <div className='mb-x1 w-full'>
          <Label>Username</Label>
          <Input
            className='mt-x1'
            name='username'
            type='text'
            required
            maxLength={16}
            value={startsWithUUID.test(user.getUsername()) ? '' : user.getUsername()}
            error={Boolean(usernameError) || (visitedFields.username && hasError('getUsername'))}
            onChange={setUsername}
            onBlur={markFieldAsVisited}
            onKeyDown={markFieldAsVisited}
          />
        </div>
      )}
      {user.isTeacher() && (
        <SchoolAssociation
          schoolError={visitedFields.school ? getSchoolError() : undefined}
          school={school}
          isTeacher={user.isTeacher()}
          onSchoolClick={setSchool}
        />
      )}
      <Button className='w-full' color='primary' type='submit' onClick={handleSubmit}>
        Update my account
      </Button>
      {user.isTeacher() && (
        <div className='mt-x1'>
          <span>Can&apos;t find your school? </span>
          <a
            target='_blank'
            rel='noopener noreferrer'
            href='https://albert-licenses.typeform.com/to/QtQ9gHSv'
          >
            Send us your school&apos;s name and address.
          </a>
        </div>
      )}
    </form>
  );
};

export default UpdateAccount;
